import React, {PureComponent} from 'react';
import * as THREE from 'three';

import Tile from './tile';

const polarLatitude = (Math.sin(3 * Math.PI / 8) + 3 / 4) / 2,
    temperateLatitude = (Math.sin(Math.PI / 4) + 1 / 2) / 2,
    tropicalLatitude = (Math.sin(Math.PI / 8) + 1 / 4) / 2,
    getBiomeId = (point, noise) => {
        const level = Math.min(Math.floor(Math.abs(noise) * 4), 3),
            latitude = Math.abs(point[1]);
        if (noise > 0) {
            if (latitude > polarLatitude) {
                return `polar-${level}`
            }
            if (latitude > temperateLatitude) {
                return `temperate-${level}`
            }
            if (latitude > tropicalLatitude) {
                return `tropical-${level}`
            }
            return `equatorial-${level}`
        }
        return `ocean-${level}`;
    };

export default class Planet extends PureComponent {
    state = {
        tiles: []
    }

    componentDidMount() {
        this.setTiles(this.props);
    }

    setTiles = ({
        noise,
        normals,
        points,
        randoms,
        regions,
        vertices
    }) => this.setState({
        tiles: points.map((point, p) => ({
            biomeId: getBiomeId(point, noise[p]),
            noise: noise[p],
            normal: normals[p],
            point: point,
            random: randoms[p],
            region: regions[p].map(v => vertices[v])
        }))
    })

    componentWillReceiveProps({
        loading,
        ...props
    }) {
        if (!loading && this.props.loading) {
            this.setTiles(props);
        }
    }

    render() {
        const {elevation, position, radius, rotation} = this.props, {tiles} = this.state;
        return <group position={position} rotation={rotation}>
            <PlanetTiles center={position} elevation={elevation} radius={radius} tiles={tiles}/>
        </group>;
    }
}

export class PlanetTiles extends PureComponent {
    render() {
        const {elevation, center, radius, tiles} = this.props;
        return <group>
            {
                tiles.map(
                    (tile, t) => <Tile key={t} center={center} elevation={elevation} radius={radius} {...tile}/>
                )
            }
        </group>;
    }
}

export class PlanetResources extends PureComponent {
    render() {
        return <resources>
            <texture
                key="texture-grass"
                resourceId="texture-grass"
                url="textures/grass.jpg"
                wrapS={THREE.RepeatWrapping}
                wrapT={THREE.RepeatWrapping}/>
            <texture
                key="texture-rock"
                resourceId="texture-rock"
                url="textures/rock.jpg"
                wrapS={THREE.RepeatWrapping}
                wrapT={THREE.RepeatWrapping}/>
            <texture
                key="texture-sand"
                resourceId="texture-sand"
                url="textures/sand.jpg"
                wrapS={THREE.RepeatWrapping}
                wrapT={THREE.RepeatWrapping}/>
            <texture
                key="texture-snow"
                resourceId="texture-snow"
                url="textures/snow.jpg"
                wrapS={THREE.RepeatWrapping}
                wrapT={THREE.RepeatWrapping}/>
            <texture
                key="texture-water"
                resourceId="texture-water"
                url="textures/water.jpg"
                wrapS={THREE.RepeatWrapping}
                wrapT={THREE.RepeatWrapping}/>
            <meshStandardMaterial
                key="water-surface"
                resourceId="water-surface"
                color={0xBBDEFB}
                opacity={0.9}
                side={THREE.DoubleSide}
                transparent={true}>
                <textureResource resourceId="texture-water"/>
            </meshStandardMaterial>
            <meshLambertMaterial key="ocean-0" resourceId="ocean-0" color={0xBBDEFB}>
                <textureResource resourceId="texture-sand"/>
            </meshLambertMaterial>
            <meshLambertMaterial key="ocean-1" resourceId="ocean-1" color={0x8FACC2}>
                <textureResource resourceId="texture-sand"/>
            </meshLambertMaterial>
            <meshLambertMaterial key="ocean-2" resourceId="ocean-2" color={0x637988}>
                <textureResource resourceId="texture-rock"/>
            </meshLambertMaterial>
            <meshLambertMaterial key="ocean-3" resourceId="ocean-3" color={0x37474F}>
                <textureResource resourceId="texture-rock"/>
            </meshLambertMaterial>
            <meshLambertMaterial key="polar-0" resourceId="polar-0" color={0x757575}>
                <textureResource resourceId="texture-rock"/>
            </meshLambertMaterial>
            <meshLambertMaterial key="polar-1" resourceId="polar-1" color={0xA3A3A3}>
                <textureResource resourceId="texture-rock"/>
            </meshLambertMaterial>
            <meshLambertMaterial key="polar-2" resourceId="polar-2" color={0xD1D1D1}>
                <textureResource resourceId="texture-rock"/>
            </meshLambertMaterial>
            <meshLambertMaterial key="polar-3" resourceId="polar-3" color={0xFFFFFF}>
                <textureResource resourceId="texture-snow"/>
            </meshLambertMaterial>
            <meshLambertMaterial key="temperate-0" resourceId="temperate-0" color={0xA39D8A}>
                <textureResource resourceId="texture-rock"/>
            </meshLambertMaterial>
            <meshLambertMaterial key="temperate-1" resourceId="temperate-1" color={0xAFB19A}>
                <textureResource resourceId="texture-rock"/>
            </meshLambertMaterial>
            <meshLambertMaterial key="temperate-2" resourceId="temperate-2" color={0xBAC4AA}>
                <textureResource resourceId="texture-grass"/>
            </meshLambertMaterial>
            <meshLambertMaterial key="temperate-3" resourceId="temperate-3" color={0xC6D8BA}>
                <textureResource resourceId="texture-grass"/>
            </meshLambertMaterial>
            <meshLambertMaterial key="tropical-0" resourceId="tropical-0" color={0xD1C49E}>
                <textureResource resourceId="texture-sand"/>
            </meshLambertMaterial>
            <meshLambertMaterial key="tropical-1" resourceId="tropical-1" color={0xBBBE90}>
                <textureResource resourceId="texture-grass"/>
            </meshLambertMaterial>
            <meshLambertMaterial key="tropical-2" resourceId="tropical-2" color={0xA4B882}>
                <textureResource resourceId="texture-grass"/>
            </meshLambertMaterial>
            <meshLambertMaterial key="tropical-3" resourceId="tropical-3" color={0x8EB274}>
                <textureResource resourceId="texture-grass"/>
            </meshLambertMaterial>
            <meshLambertMaterial key="equatorial-0" resourceId="equatorial-0" color={0xFFECB3}>
                <textureResource resourceId="texture-sand"/>
            </meshLambertMaterial>
            <meshLambertMaterial key="equatorial-1" resourceId="equatorial-1" color={0xC6CC87}>
                <textureResource resourceId="texture-grass"/>
            </meshLambertMaterial>
            <meshLambertMaterial key="equatorial-2" resourceId="equatorial-2" color={0x8EAB5B}>
                <textureResource resourceId="texture-grass"/>
            </meshLambertMaterial>
            <meshLambertMaterial key="equatorial-3" resourceId="equatorial-3" color={0x558B2F}>
                <textureResource resourceId="texture-grass"/>
            </meshLambertMaterial>
        </resources>;
    }
}


// WEBPACK FOOTER //
// ./src/apps/scene-planet/scene/planet/index.js